// components/entities/CampaignRenderer.tsx
import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { Building2, Layers, Target } from '@/components/ui/lucide-memo';
import type { BaseEntityProps, CampaignDTO, CampaignTargetKind, EntityRowAction } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { portalTableRow } from '@/components/portal/portalTheme';
import { campaignLabel, campaignResultCounts, campaignStatusPillClass, formatCampaignDateTime, isCampaignActive } from '#lib/leasing/campaignCard';
import StopButton, { STOP_BUTTON_SIZE_INLINE } from '@/components/portal/StopButton';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';
import { useTableGlass, TABLE_GLASS_ROW } from '../portal/tableSurface';

const DEFAULT_ROW_COLUMN_ORDER = ['campaign', 'granularity', 'status', 'targets', 'results', 'updatedAt', 'actions'] as const;

// One frozen style object per alignment: the row maps over its columns, so a literal here would
// hand every cell a brand-new object on every render.
const CELL_JUSTIFY: Record<'start' | 'center' | 'end', React.CSSProperties> = {
  start: { justifyContent: 'flex-start' },
  center: { justifyContent: 'center' },
  end: { justifyContent: 'flex-end' },
};

const translations = {
  en: {
    untitled: 'Campaign',
    granularity: { PORTFOLIO: 'Portfolio', ASSET: 'Asset' } as Record<CampaignTargetKind, string>,
    status: {
      PENDING: 'Pending',
      WAITING: 'Queued',
      RUNNING: 'Running',
      COMPLETE: 'Complete',
      FAILED: 'Failed',
      ABORTED: 'Aborted',
    } as Record<string, string>,
    hiddenSuffix: (hidden: number) => `${hidden} hidden`,
    stop: 'Abort',
    stopping: 'Aborting...',
    stopFailed: 'Failed to stop campaign',
  },
  fr: {
    untitled: 'Campagne',
    granularity: { PORTFOLIO: 'Portefeuille', ASSET: 'Actif' } as Record<CampaignTargetKind, string>,
    status: {
      PENDING: 'En attente',
      WAITING: 'En file',
      RUNNING: 'En cours',
      COMPLETE: 'Terminee',
      FAILED: 'Echouee',
      ABORTED: 'Interrompue',
    } as Record<string, string>,
    hiddenSuffix: (hidden: number) => `${hidden} masquee(s)`,
    stop: 'Arreter',
    stopping: 'Arret...',
    stopFailed: "Echec de l'arret de la campagne",
  },
};

const GRANULARITY_ICON: Record<CampaignTargetKind, typeof Target> = {
  PORTFOLIO: Layers,
  ASSET: Building2,
};

export interface CampaignRendererProps extends BaseEntityProps<CampaignDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<CampaignDTO>[];
}

/**
 * One row for a campaign at any granularity, read from the unified `Campaign` projection.
 *
 * Renders from `CampaignDTO` alone and never syncs a legacy row, which is the whole point: a
 * surface listing from `Campaign` used to render blank because `EntityView` had no case for the
 * type and the legacy renderers read registries the unified list never fills.
 *
 * Deliberately carries NO bespoke stop/destroy/re-apply buttons. Those live on the three legacy
 * renderers, are genuinely different per family (portfolio-fit has no kernel stop path at all), and
 * nothing renders a `Campaign` row yet, so reimplementing them here would be untested duplication
 * of exactly the kind this unification exists to remove. `variant="page"` delegates to the legacy
 * page, which has them; the row exposes the generic `EntityRowActions` only.
 */
export const CampaignRenderer = React.memo(({
  entityId,
  variant = 'card',
  onClick,
  className,
  columns,
  rowActions = [],
}: CampaignRendererProps) => {
  const glass = useTableGlass();
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const data = useKernelStore((state) => state.inventory.Campaign.registry[entityId]) as CampaignDTO | undefined;
  const normalizedVariant = variant === 'page' ? 'row' : variant;
  const [isStopping, setIsStopping] = useState(false);

  const handleStop = useCallback(async () => {
    if (!kernel.current || !data || isStopping) return;
    setIsStopping(true);
    try {
      // ONE ENGINE, ONE STOP. The dispatch on granularity went with the portfolio-wide path on
      // 2026-08-18: its route no longer exists, and the only campaigns anyone can start are asset
      // runs. Historical PORTFOLIO rows are all finished, so there is nothing to stop on them.
      await kernel.current.stopSourcingCampaign(data.id);
    } catch (error) {
      kernel.current.handleError(error instanceof Error ? error.message : t.stopFailed);
    } finally {
      setIsStopping(false);
    }
  }, [kernel, data, isStopping, t]);

  const formatDate = useCallback((value?: string) => formatCampaignDateTime(value, lang), [lang]);

  // The row itself is clickable, so the stop button has to swallow the click. A stable reference
  // keeps the button out of the re-render path.
  const handleStopClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void handleStop();
  }, [handleStop]);

  const handleRowClick = useCallback(() => {
    if (onClick) {
      onClick();
      return;
    }
    if (!kernel.current) return;
    // openSourceCampaign, NOT focusEntityUI: it resolves the campaign's own bound conversation.
    // focusEntityUI has no scoped home for a campaign type (useLeasingWorkflows.ts:533-540 assigns
    // one only to Project, or to Portfolio/Property/Unit under ORCHESTRATE_SOURCING), so it would
    // leave whatever chat happened to be active sitting next to the shortlist. This row IS
    // reachable today, from the agent's campaign list (SOURCE_SHOW_IN_CHAT).
    void kernel.current.openSourceCampaign(entityId);
  }, [onClick, kernel, entityId]);

  if (!data) return <EntitySkeleton variant={normalizedVariant} className={className} />;

  const label = campaignLabel(data, t.untitled);
  const status = data.status || 'PENDING';
  const { visible, hidden } = campaignResultCounts(data);
  const targetCount = data.targetBrandCount ?? 0;
  const GranularityIcon = GRANULARITY_ICON[data.targetKind] ?? Target;

  if (normalizedVariant === 'chip') {
    return (
      <div
        className={clsx('flex items-center gap-2 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs text-white/80 cursor-pointer hover:bg-white/10', className)}
        onClick={handleRowClick}
      >
        <GranularityIcon size={12} />
        <span className="truncate">{label}</span>
      </div>
    );
  }

  if (normalizedVariant === 'card') {
    return (
      <div
        className={clsx('flex flex-col w-full h-full overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:bg-white/10 hover:border-white/20 cursor-pointer', className)}
        onClick={handleRowClick}
      >
        <div className="p-3 flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-500/20 text-indigo-400">
            <GranularityIcon size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] text-white/40">
              {t.granularity[data.targetKind]} - {t.status[status] ?? status}
            </div>
            <div className="text-xs font-medium text-white/90 truncate">{label}</div>
          </div>
        </div>
      </div>
    );
  }

  const cellRenderers: Record<string, React.ReactNode> = {
    campaign: (
      <div className="min-w-0 h-full flex items-center gap-2">
        <GranularityIcon size={12} className="shrink-0 text-white/45" />
        <span className="truncate font-medium text-white" title={label}>{label}</span>
      </div>
    ),
    granularity: (
      <div className="flex h-full items-center text-white/60">
        <span className="truncate">{t.granularity[data.targetKind] ?? data.targetKind}</span>
      </div>
    ),
    status: (
      <div className="flex h-full items-center">
        <span className={clsx('rounded-full border px-2 py-0.5 text-[10px]', campaignStatusPillClass(status))}>
          {t.status[status] ?? status}
        </span>
      </div>
    ),
    targets: (
      <div className="flex h-full items-center justify-end text-white/85">{targetCount}</div>
    ),
    results: (
      // "-" when the route resolved no count, which is not the same as a run that found nothing.
      <div className="flex h-full items-center justify-end gap-1 text-white/85">
        <span>{visible === null ? '-' : visible}</span>
        {hidden > 0 && <span className="text-[10px] text-white/40">({t.hiddenSuffix(hidden)})</span>}
      </div>
    ),
    updatedAt: (
      <div className="flex h-full items-center text-white/60">
        <span className="truncate">{formatDate(data.updatedAt)}</span>
      </div>
    ),
    actions: (
      <div className="flex h-full items-center justify-end gap-2">
        {isCampaignActive(data) && (
          <StopButton
            onClick={handleStopClick}
            disabled={isStopping}
            size={STOP_BUTTON_SIZE_INLINE}
            label={isStopping ? t.stopping : t.stop}
          />
        )}
        <EntityRowActions entityType="Campaign" entity={data} rowActions={rowActions} />
      </div>
    ),
  };

  const activeColumns: TableColumnDef[] = columns ?? DEFAULT_ROW_COLUMN_ORDER.map((id) => ({ id, header: '', minWidth: 1 }));

  return (
    <div
      className={clsx(
        portalTableRow,
        styles.gridRow,
        styles.bodyRow,
        'group h-10 w-full cursor-pointer border-b border-[#2A2A2A] bg-[#181818] hover:bg-[#1E1E1E]',
        glass && TABLE_GLASS_ROW,
        className
      )}
      onClick={handleRowClick}
    >
      {activeColumns.map((column) => (
        <div
          key={column.id}
          className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
          style={
            column.id === 'actions' || column.align === 'right'
              ? CELL_JUSTIFY.end
              : column.align === 'center'
                ? CELL_JUSTIFY.center
                : CELL_JUSTIFY.start
          }
        >
          {cellRenderers[column.id] || null}
        </div>
      ))}
    </div>
  );
});

CampaignRenderer.displayName = 'CampaignRenderer';
